import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import Flow from '@flowjs/flow.js';
import { PushPipe } from '@ngrx/component';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AttachmentsActions, AttachmentsSelectors } from '@recruitee/attachments-data';
import type { Attachment } from '@recruitee/attachments-types';
import { filterBoolean } from '@recruitee/common';
import { AttachmentsListComponent } from '@recruitee/components/attachments-list';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  GroupComponent,
  NotificationsController,
  ProtectedControlDirective,
} from '@recruitee/user-interface';
import {
  asViewRef,
  generateUuid,
  getHumanReadableFileSize,
  MaxFileSize,
  validateMaxFileSize,
} from '@recruitee/utils';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import { InterviewResultsModule } from '../interview-results.module';

interface FlowFile {
  name: string;
  file: File;
}

@Component({
  selector: 'rt-interview-file',
  templateUrl: 'interview-file.component.html',
  styleUrls: ['./interview-file.component.less'],
  standalone: true,
  imports: [
    InterviewResultsModule,
    ProtectedControlDirective,
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
    PushPipe,
    AttachmentsListComponent,
    InterviewBaseBoxComponent,
  ],
})
export class InterviewFileComponent implements AfterViewInit {
  @ViewChild('fileSelectButton', { static: true }) public fileSelectButton: ButtonComponent;

  @Input() public questionForm: UntypedFormGroup;
  @Input() public inputFiles: Attachment[] = [];
  @Input() public edit: boolean = false;
  @Input() public formSubmissionAttempt: boolean = false;
  @Input() public canManageCandidates: boolean = false;

  @Output() public attachmentsChanged: EventEmitter<any> = new EventEmitter();
  @Output() public filesUploadingChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  public flow: any;
  public uploading: number = 0;
  public files: Attachment[] = [];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private notificationsCtrl: NotificationsController,
    private i18n: I18nService,
    private actions$: Actions,
    private store: Store,
  ) {}

  private get attachmentIdsControl(): UntypedFormArray {
    return <UntypedFormArray>this.questionForm.get('attachmentIds');
  }

  public ngOnInit(): void {
    const flowOptions = {
      singleFile: false,
      testChunks: false,
      fileParameterName: 'attachment',
      allowDuplicateUploads: true,
    };

    this.flow = new Flow(flowOptions);
    this.init();
    this.handleFileRemovalSuccess();
  }

  public ngAfterViewInit(): void {
    this.initFormControls();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.inputFiles?.currentValue) {
      this.files = this.inputFiles || [];
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public dispatchDeleteFile(file: Attachment): void {
    this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: file.id }));
    this.increaseUploading();
  }

  public dispatchDeleteAllFiles(): void {
    if (this.files.length) {
      this.store.dispatch(
        AttachmentsActions.deleteManyAttachments({ payload: this.files.map(file => file.id) }),
      );
      this.increaseUploading();
    }
  }

  private initFormControls(): void {
    this.attachmentIdsControl.clear();
    this.files.forEach(file => this.attachmentIdsControl.push(new UntypedFormControl(file.id)));
    this.cdRef.detectChanges();
  }

  private handleFileRemovalSuccess(): void {
    this.actions$
      .pipe(
        ofType(
          AttachmentsActions.deleteAttachmentSuccess,
          AttachmentsActions.deleteManyAttachmentsSuccess,
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(result => {
        result.type === AttachmentsActions.deleteManyAttachmentsSuccess.type
          ? this.deleteAllFiles()
          : this.removeFromList(result.payload.data);
        this.decreaseUploading();
      });
  }

  private init(): void {
    this.flow.assignBrowse(this.fileSelectButton.elRef.nativeElement, false, false);
    this.bindEvents();
  }

  private bindEvents(): void {
    this.flow.on('filesSubmitted', (files: FlowFile[]) => this.submitFiles(files));
  }

  private submitFiles(flowFiles: FlowFile[]): void {
    if (flowFiles && flowFiles.length && !this.uploading) {
      this.uploading = 0;
      this.filesUploadingChanged.emit(false);
      flowFiles.forEach(this.uploadFile.bind(this));
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private uploadFile(flowFile: FlowFile): void {
    const uuid = generateUuid();
    const { file } = flowFile;
    const validSize = validateMaxFileSize(file, MaxFileSize.File50MB);

    if (!validSize) {
      const message = this.i18n.translate('app.common.errors.file_size_limit_exceeded', {
        limit: getHumanReadableFileSize(MaxFileSize.File50MB),
        fileSize: getHumanReadableFileSize(file.size),
      });
      this.notificationsCtrl.error(message);

      return;
    }

    this.increaseUploading();
    this.cdRef.detectChanges();
    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: {
          file,
          correlationId: uuid,
        },
      }),
    );

    this.store
      .select(AttachmentsSelectors.byId(uuid))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(attachment => {
        this.afterUpload(attachment);
      });

    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
        first(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(error => {
        if (uuid === error.payload.correlationId) {
          this.onError();
        }
      });
  }

  private deleteAllFiles(): void {
    this.files = [];
    this.attachmentIdsControl.clear();
    this.attachmentIdsControl.markAsPristine();
    this.attachmentIdsControl.markAsUntouched();
    this.attachmentsChanged.emit(this.files);
  }

  private removeFromList(deletedFile: Attachment): void {
    const files = this.files.filter(file => file.id !== deletedFile.id);

    this.attachmentIdsControl.removeAt(
      this.attachmentIdsControl.value.findIndex((id: number) => id === deletedFile.id),
    );
    this.attachmentIdsControl.markAsDirty();
    this.attachmentsChanged.emit(files);
    this.files = files;

    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  private afterUpload(file: Attachment): void {
    this.attachmentIdsControl.push(new UntypedFormControl(file.id));
    this.attachmentIdsControl.markAsDirty();
    this.files = [...this.files, file];
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
    this.attachmentsChanged.emit(this.files);
    this.decreaseUploading();
  }

  private onError(): void {
    this.decreaseUploading();
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  private increaseUploading(): void {
    this.uploading++;
    this.filesUploadingChanged.emit(true);
  }

  private decreaseUploading(): void {
    this.uploading--;

    if (this.uploading <= 0) {
      this.filesUploadingChanged.emit(false);
    }
  }
}
